<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'

const isMenuOpen = ref(false)
const isComputeOpen = ref(false)
const isPlatformOpen = ref(false)
const isCompanyOpen = ref(false)

const computeServices = [
  { name: 'Dedicated CPU', path: '/compute/dedicated-cpu', description: 'High-performance dedicated CPU instances', icon: 'fas fa-microchip' },
  { name: 'Shared CPU', path: '/compute/shared-cpu', description: 'Cost-effective shared CPU resources', icon: 'fas fa-server' },
  { name: 'Dedicated GPU', path: '/compute/dedicated-gpu', description: 'Powerful GPU instances for ML and rendering', icon: 'fas fa-desktop' },
  { name: 'Bare Metal', path: '/compute/bare-metal', description: 'Raw hardware access for maximum performance', icon: 'fas fa-layer-group' },
  { name: 'Colocation', path: '/compute/colocation', description: 'Bring your own hardware to our facilities', icon: 'fas fa-building' }
]

const platformServices = [
  { name: 'Services', path: '/services', description: 'Explore our cloud services', icon: 'fas fa-cloud' },
  { name: 'Data Centers', path: '/data-centers', description: 'Global infrastructure network', icon: 'fas fa-globe' },
  { name: 'Security & Compliance', path: '/security', description: 'Our security measures and certifications', icon: 'fas fa-shield-alt' },
  { name: 'Documentation', path: '/docs', description: 'Platform and API documentation', icon: 'fas fa-book' },
  { name: 'Integrations', path: '/integrations', description: 'Connect your favorite tools and services', icon: 'fas fa-plug' }
]

const companyMenu: Record<string, { name: string; path: string; icon: string }[]> = {
  'About Us': [
    { name: 'Our Story', path: '/about/story', icon: 'fas fa-book-open' },
    { name: 'Mission & Values', path: '/about/mission', icon: 'fas fa-bullseye' },
    { name: 'Careers', path: '/careers', icon: 'fas fa-briefcase' }
  ],
  'Media': [
    { name: 'Press Releases', path: '/media/press', icon: 'fas fa-newspaper' },
    { name: 'Media Kit', path: '/media/media-kit', icon: 'fas fa-camera' },
    { name: 'Awards', path: '/media/awards', icon: 'fas fa-trophy' }
  ],
  'Legal': [
    { name: 'Terms of Service', path: '/legal/terms', icon: 'fas fa-gavel' },
    { name: 'Privacy Policy', path: '/legal/privacy', icon: 'fas fa-user-shield' },
    { name: 'Acceptable Use', path: '/legal/acceptable-use', icon: 'fas fa-check-circle' }
  ],
  'Contact': [
    { name: 'General Inquiries', path: '/contact', icon: 'fas fa-envelope' }
  ]
}

const mainNavigation = [
  { name: 'Blog', path: '/blog' },
  { name: 'Status', path: '/status' }
]
</script>

<template>
  <nav class="fixed w-full z-50 top-0 glass-card border-b border-white/10">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex items-center">
          <RouterLink to="/" class="flex items-center group">
            <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
              <i class="fas fa-cloud text-white text-lg"></i>
            </div>
            <span class="text-2xl font-bold gradient-text">Syniac</span>
          </RouterLink>

          <div class="hidden md:ml-10 md:flex md:space-x-1">
            <!-- Compute Dropdown -->
            <Menu as="div" class="relative">
              <MenuButton class="btn-ghost inline-flex items-center px-4 py-2 text-sm font-medium">
                <span>Compute</span>
                <i class="fas fa-chevron-down ml-2 text-xs transition-transform duration-200"></i>
              </MenuButton>

              <MenuItems class="absolute left-0 mt-2 w-80 glass-card rounded-2xl shadow-modern-lg ring-1 ring-white/10 divide-y divide-white/10">
                <div class="py-2">
                  <MenuItem v-for="service in computeServices" :key="service.name" v-slot="{ active }">
                    <RouterLink
                      :to="service.path"
                      :class="[
                        active ? 'bg-white/10 text-white' : 'text-gray-300',
                        'group flex items-center px-4 py-3 hover:bg-white/5 transition-colors duration-200 rounded-xl mx-2'
                      ]"
                    >
                      <i :class="[service.icon, 'w-6 text-primary-400']"></i>
                      <div class="ml-3">
                        <p class="font-medium">{{ service.name }}</p>
                        <p class="text-sm text-gray-400">{{ service.description }}</p>
                      </div>
                    </RouterLink>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>

            <!-- Platform Dropdown -->
            <Menu as="div" class="relative">
              <MenuButton class="btn-ghost inline-flex items-center px-4 py-2 text-sm font-medium">
                <span>Platform</span>
                <i class="fas fa-chevron-down ml-2 text-xs transition-transform duration-200"></i>
              </MenuButton>

              <MenuItems class="absolute left-0 mt-2 w-80 glass-card rounded-2xl shadow-modern-lg ring-1 ring-white/10 divide-y divide-white/10">
                <div class="py-2">
                  <MenuItem v-for="service in platformServices" :key="service.name" v-slot="{ active }">
                    <RouterLink
                      :to="service.path"
                      :class="[
                        active ? 'bg-white/10 text-white' : 'text-gray-300',
                        'group flex items-center px-4 py-3 hover:bg-white/5 transition-colors duration-200 rounded-xl mx-2'
                      ]"
                    >
                      <i :class="[service.icon, 'w-6 text-primary-400']"></i>
                      <div class="ml-3">
                        <p class="font-medium">{{ service.name }}</p>
                        <p class="text-sm text-gray-400">{{ service.description }}</p>
                      </div>
                    </RouterLink>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>

            <!-- Company Dropdown -->
            <Menu as="div" class="relative">
              <MenuButton class="btn-ghost inline-flex items-center px-4 py-2 text-sm font-medium">
                <span>Company</span>
                <i class="fas fa-chevron-down ml-2 text-xs transition-transform duration-200"></i>
              </MenuButton>

              <MenuItems class="absolute left-0 mt-2 w-[400px] glass-card rounded-2xl shadow-modern-lg ring-1 ring-white/10">
                <div class="grid grid-cols-2 gap-4 p-6">
                  <div v-for="(items, category) in companyMenu" :key="category" class="space-y-4">
                    <h3 class="text-sm font-semibold text-primary-400 uppercase tracking-wider">{{ category }}</h3>
                    <ul class="space-y-2">
                      <MenuItem v-for="item in items" :key="item.name" v-slot="{ active }">
                        <RouterLink
                          :to="item.path"
                          :class="[
                            active ? 'bg-white/10 text-white' : 'text-gray-300',
                            'group flex items-center px-3 py-2 text-sm rounded-xl hover:bg-white/5 transition-colors duration-200'
                          ]"
                        >
                          <i :class="[item.icon, 'w-5 text-primary-400']"></i>
                          <span class="ml-3">{{ item.name }}</span>
                        </RouterLink>
                      </MenuItem>
                    </ul>
                  </div>
                </div>
              </MenuItems>
            </Menu>

            <RouterLink
              v-for="item in mainNavigation"
              :key="item.name"
              :to="item.path"
              class="btn-ghost inline-flex items-center px-4 py-2 text-sm font-medium"
            >
              {{ item.name }}
            </RouterLink>
          </div>
        </div>

        <div class="hidden md:flex items-center space-x-4">
          <RouterLink
            to="/contact"
            class="btn-secondary"
          >
            Contact Sales
          </RouterLink>
          <a
            href="https://billing.syniac.com"
            target="_blank"
            rel="noopener noreferrer"
            class="btn-primary"
          >
            Login
          </a>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center">
          <button @click="isMenuOpen = !isMenuOpen" class="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-200">
            <i :class="[isMenuOpen ? 'fas fa-times' : 'fas fa-bars']" class="text-xl"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <div
      :class="[
        'md:hidden glass-card border-t border-white/10 transition-all duration-300 ease-in-out overflow-y-auto max-h-screen',
        isMenuOpen ? 'block' : 'hidden'
      ]"
    >
      <div class="px-4 pt-4 pb-6 space-y-2">
        <!-- Mobile Compute Menu -->
        <div class="px-2 py-2">
          <button @click="isComputeOpen = !isComputeOpen" class="flex items-center justify-between w-full text-gray-300 font-medium mb-2 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-white/5">
            <span>Compute Services</span>
            <i :class="[isComputeOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down', 'text-xs transition-transform duration-200']"></i>
          </button>
          <div v-show="isComputeOpen" class="space-y-1 pl-4">
            <RouterLink
              v-for="service in computeServices"
              :key="service.name"
              :to="service.path"
              class="flex items-center text-gray-400 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors duration-200"
              @click="isMenuOpen = false"
            >
              <i :class="[service.icon, 'w-6 text-primary-400']"></i>
              <span class="ml-2">{{ service.name }}</span>
            </RouterLink>
          </div>
        </div>

        <!-- Mobile Platform Menu -->
        <div class="px-2 py-2">
          <button @click="isPlatformOpen = !isPlatformOpen" class="flex items-center justify-between w-full text-gray-300 font-medium mb-2 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-white/5">
            <span>Platform</span>
            <i :class="[isPlatformOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down', 'text-xs transition-transform duration-200']"></i>
          </button>
          <div v-show="isPlatformOpen" class="space-y-1 pl-4">
            <RouterLink
              v-for="service in platformServices"
              :key="service.name"
              :to="service.path"
              class="flex items-center text-gray-400 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors duration-200"
              @click="isMenuOpen = false"
            >
              <i :class="[service.icon, 'w-6 text-primary-400']"></i>
              <span class="ml-2">{{ service.name }}</span>
            </RouterLink>
          </div>
        </div>

        <!-- Mobile Company Menu -->
        <div class="px-2 py-2">
          <button @click="isCompanyOpen = !isCompanyOpen" class="flex items-center justify-between w-full text-gray-300 font-medium mb-2 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-white/5">
            <span>Company</span>
            <i :class="[isCompanyOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down', 'text-xs transition-transform duration-200']"></i>
          </button>
          <div v-show="isCompanyOpen" class="space-y-2 pl-4">
            <div v-for="(items, category) in companyMenu" :key="category" class="mb-4">
              <div class="text-gray-300 font-medium mb-2 text-sm">{{ category }}</div>
              <div class="space-y-1 pl-4">
                <RouterLink
                  v-for="item in items"
                  :key="item.name"
                  :to="item.path"
                  class="flex items-center text-gray-400 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors duration-200"
                  @click="isMenuOpen = false"
                >
                  <i :class="[item.icon, 'w-6 text-primary-400']"></i>
                  <span class="ml-2">{{ item.name }}</span>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>

        <RouterLink
          v-for="item in mainNavigation"
          :key="item.name"
          :to="item.path"
          class="block px-4 py-3 text-base font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors duration-200"
          @click="isMenuOpen = false"
        >
          {{ item.name }}
        </RouterLink>

        <div class="px-2 pt-4 space-y-3">
          <RouterLink
            to="/contact"
            class="block w-full text-center btn-secondary"
            @click="isMenuOpen = false"
          >
            Contact Sales
          </RouterLink>
          <a
            href="https://billing.syniac.com"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-full text-center btn-primary"
            @click="isMenuOpen = false"
          >
            Login
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>